import {
  CondensedButton,
  Pill,
  Spinner,
  Table,
  Tooltip,
} from "@instructure/ui";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { Files } from "../../../services/FilesService";
import { ReviewService } from "../../../services/ReviewService";
import { TermsContext } from "../../../utils/Context";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { sortTermsDescending } from "../../TermManagement/utils";
import { CourseReviewContext } from "../CourseReview";
import { INITIAL_COURSE_REVIEW } from "../utils";

const PENDING = "pending";
const IN_PROGRESS = "in progress";
const PUBLISHED = "published";

const CourseReviewEntry = ({ review, status }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { insightsTerms } = useContext(TermsContext);
  const { setDraftData } = useContext(CourseReviewContext);
  const location = useLocation();
  const { section, cycle } = location.state;
  const navigate = useNavigate();

  const [relevantTerms, setRelevantTerms] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);

  const startYear = useMemo(
    () => review.academic_years[0],
    [review.academic_years]
  );
  const trueEndYear = useCallback(() => {
    for (let i = 0; i < review.academic_years.length; i++) {
      if (review.academic_years[i] === null) {
        return review.academic_years[i - 1];
      }
    }
    return review.academic_years[review.academic_years.length - 1];
  }, [review.academic_years]);
  const endYear = review.academic_years[review.academic_years.length - 1];

  useEffect(() => {
    const trueEndYearValue = trueEndYear();
    if (startYear && trueEndYearValue) {
      const startDate = new Date(startYear.start_at);
      const endDate = new Date(trueEndYearValue.end_at);
      const filteredTerms = insightsTerms.filter((term) => {
        const termDate = new Date(term.start_at);
        return termDate >= startDate && termDate <= endDate;
      });
      const sortedTerms = sortTermsDescending(filteredTerms);
      setRelevantTerms(sortedTerms);
    }
  }, [insightsTerms, startYear, trueEndYear]);

  async function fetchStats() {
    try {
      const stats = await ReviewService.getReviewStatistics(review.id);
      return stats;
    } catch {
      throw new Error("Failed to fetch review statistics");
    }
  }

  async function handleLoadReview() {
    let stats;
    setDraftLoading(true);

    try {
      stats = await fetchStats();
    } catch {
      showNotification(
        t("notifications.general.fetchError", {
          obj: t("courseReview.courseReview"),
        }),
        ERROR
      );
      setDraftLoading(false);
      return;
    }

    try {
      if (review.shareable_code) {
        const tenantId = window.location.hostname.split(".")[0];
        const courseReview = await Files.getPublicFileContent(
          "response.json",
          "public-reviews",
          ReviewService.getReviewFolderPath(
            review.cycle.ddl_id,
            review.reviewable.ddl_id,
            review.id
          ),
          tenantId === "localhost" ? "ddl-local" : tenantId
        );
        const courseReviewObj = JSON.parse(courseReview.content);
        setDraftData(courseReviewObj);

        navigate("/course-review/summary", {
          state: {
            section: section,
            cycle: cycle,
            review: review,
            stats: stats,
          },
        });
      } else {
        const draft = await Files.getFileWithPresignedGetUrl(
          "response.json",
          "reviewDrafts",
          ReviewService.getReviewFolderPath(
            review.cycle.ddl_id,
            review.reviewable.ddl_id,
            review.id
          )
        );

        if (draft) {
          setDraftData(draft);
          sessionStorage.setItem(
            "courseReview/" + review.id,
            JSON.stringify(draft)
          );
        }
        navigate("/course-review/data-view", {
          state: {
            section: section,
            cycle: cycle,
            relevantTerms: relevantTerms,
            review: review,
            stats: stats,
          },
        });
      }
    } catch (error) {
      if (error.name === "SyntaxError") {
        setDraftData(INITIAL_COURSE_REVIEW);
        sessionStorage.removeItem("courseReview/" + cycle.id);
        navigate("/course-review/data-view", {
          state: {
            section: section,
            cycle: cycle,
            relevantTerms: relevantTerms,
            review: review,
            stats: stats,
          },
        });
      } else {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("courseReview.courseReview"),
          }),
          ERROR
        );
      }
    } finally {
      setDraftLoading(false);
    }
  }

  function actionText(status) {
    switch (status) {
      case PENDING:
        return t("courseReview.start");
      case IN_PROGRESS:
        return t("global.continue");
      case PUBLISHED:
        return t("courseReview.view");
      default:
        return "";
    }
  }

  return (
    <Table.Row>
      <Table.Cell>
        {startYear?.name ? (
          <Pill color="info">{startYear.name}</Pill>
        ) : (
          <Pill color="warning">{t("global.na")}</Pill>
        )}
        {" - "}
        {endYear?.name ? (
          <Pill color="info">{endYear.name}</Pill>
        ) : (
          <Pill color="warning">{t("global.na")}</Pill>
        )}
      </Table.Cell>
      <Table.Cell>
        {draftLoading ? (
          <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
        ) : (
          <Tooltip
            renderTip={t("courseReview.missingAcademicYears")}
            placement="end"
            on={!startYear?.name || !endYear?.name ? ["hover", "focus"] : []}
          >
            <CondensedButton
              onClick={handleLoadReview}
              disabled={!startYear?.name || !endYear?.name}
              data-testid={`course-review-button-${startYear?.name || "na"}`}
            >
              {actionText(status)}
            </CondensedButton>
          </Tooltip>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

export default CourseReviewEntry;
